import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useEffect, useRef, useState} from 'react'

import {CopyProjectDialog, type CopyProjectDialogMode} from './CopyProjectDialog'
import {NewProjectForm} from './NewProjectForm'
import {OpenClosedProjects} from './OpenClosedProjects'
import type {ProjectListLoadingState} from './project-list-loading-state'
import type {ProjectSortValue} from './project-sort-options'
import {type ProjectListEmptyState, ProjectListView} from './ProjectListView'
import {ProjectRow} from './ProjectRow'
import {ProjectSortMenu} from './ProjectSortMenu'
import type {NewProjectAction, Project} from './types'
import {useConfirmUnlinkProject} from './use-confirm-unlink-project'
import {useUnlinkProject} from './use-unlink-project'

interface ProjectListProps {
  projects: Project[]
  openCount: number
  closedCount: number
  hasNextPage: boolean
  onLoadMore: () => void
  viewerCanPush: boolean
  /** Repo owner login, used as the form action prefix for the "New project" CTA. */
  owner: string
  /** Repo name, used as the form action prefix for the "New project" CTA. */
  repo: string
  /**
   * Hidden-field bundle for the "New project" CTA. `null` when the viewer can't
   * create a project here — the CTA is hidden in that case.
   */
  newProjectAction: NewProjectAction | null
  query: string
  /** The current open/closed filter state, used to tailor the empty-state copy. */
  state: 'open' | 'closed'
  sort: ProjectSortValue
  onSortChange: (value: ProjectSortValue) => void
  loadingState?: ProjectListLoadingState
  /** When true, the list is showing the templates view; affects blankslate copy and icon. */
  isTemplateView?: boolean
}

export function ProjectList({
  projects,
  openCount,
  closedCount,
  hasNextPage,
  onLoadMore,
  viewerCanPush,
  owner,
  repo,
  newProjectAction,
  query,
  state,
  sort,
  onSortChange,
  loadingState = 'ready',
  isTemplateView = false,
}: ProjectListProps) {
  const listItemsHeader = (
    <ListViewMetadata sectionFilters={<OpenClosedProjects openCount={openCount} closedCount={closedCount} />}>
      <ProjectSortMenu sort={sort} onSortChange={onSortChange} />
    </ListViewMetadata>
  )

  const {addToast} = useToastContext()
  const {mutate: mutateUnlinkProject} = useUnlinkProject({owner, repo})
  const {confirmUnlinkProject} = useConfirmUnlinkProject()
  // Hold `addToast` in a ref so the callback identity below stays stable across
  // toast events. The `ToastContextProvider` recreates its context value (and
  // therefore `addToast`) every time *any* toast fires anywhere in the app; without
  // this indirection, `handleUnlinkProject` would change on every such render and
  // defeat `ProjectRow`'s `memo()` for every row in the list.
  const addToastRef = useRef(addToast)
  useEffect(() => {
    addToastRef.current = addToast
  }, [addToast])
  // Owned here (not in `ProjectRowActionMenu`) so the per-call `onError` toast still
  // fires after the row optimistically unmounts. TanStack Query skips per-call
  // `mutate` callbacks when the calling component is unmounted, but `ProjectList`
  // stays mounted across cache-driven row removals. `mutate` is a stable reference
  // from `useMutation`, so this callback survives across renders.
  const handleUnlinkProject = useCallback(
    async (project: Project) => {
      const confirmed = await confirmUnlinkProject(project)
      if (!confirmed) return

      mutateUnlinkProject(
        {projectId: project.id},
        {
          onError: () => {
            addToastRef.current({
              type: 'error',
              message: `Couldn't unlink ${project.title}. Please try again.`,
            })
          },
        },
      )
    },
    [confirmUnlinkProject, mutateUnlinkProject],
  )

  const [activeCopyTarget, setActiveCopyTarget] = useState<{project: Project; mode: CopyProjectDialogMode} | null>(null)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleCopyProject = useCallback(
    (project: Project) => {
      sendAnalyticsEvent('memex_copy_project', undefined, {project_id: project.id})
      setActiveCopyTarget({project, mode: 'copy'})
    },
    [sendAnalyticsEvent],
  )

  const handleCopyAsTemplate = useCallback(
    (project: Project) => {
      sendAnalyticsEvent('memex_event', undefined, {
        project_id: project.id,
        name: 'copy_as_template',
        ui: 'project_list',
      })
      setActiveCopyTarget({project, mode: 'template'})
    },
    [sendAnalyticsEvent],
  )

  const handleCopyDialogClose = useCallback(() => {
    setActiveCopyTarget(null)
  }, [])

  return (
    <>
      <ProjectListView
        projects={projects}
        title="Repository projects"
        metadata={listItemsHeader}
        emptyState={repositoryEmptyState({viewerCanPush, owner, repo, newProjectAction, state, isTemplateView})}
        query={query}
        hasNextPage={hasNextPage}
        onLoadMore={onLoadMore}
        loadingState={loadingState}
        isTemplateView={isTemplateView}
        listTestId="repository-project-list-view"
        renderRow={project => (
          <ProjectRow
            key={project.id}
            project={project}
            onCopyProject={handleCopyProject}
            onCopyAsTemplate={handleCopyAsTemplate}
            onUnlinkProject={handleUnlinkProject}
          />
        )}
      />
      {activeCopyTarget && (
        <CopyProjectDialog
          project={activeCopyTarget.project}
          mode={activeCopyTarget.mode}
          onClose={handleCopyDialogClose}
        />
      )}
    </>
  )
}

/**
 * The closed branch suppresses the "New project" CTA: filter-driven emptiness should
 * not look like a brand-new repo prompting the viewer to create a project.
 */
function repositoryEmptyState({
  viewerCanPush,
  owner,
  repo,
  newProjectAction,
  state,
  isTemplateView,
}: Pick<
  ProjectListProps,
  'viewerCanPush' | 'owner' | 'repo' | 'newProjectAction' | 'state' | 'isTemplateView'
>): ProjectListEmptyState {
  if (state === 'closed') {
    return {heading: isTemplateView ? 'No closed project templates' : 'No closed projects'}
  }

  if (isTemplateView) {
    return {
      heading: viewerCanPush ? 'Provide quick access to relevant project templates' : 'No project templates',
      description: viewerCanPush ? 'Add project templates from your organization to view them here.' : undefined,
      announcement: 'No project templates',
    }
  }

  return {
    heading: 'No projects found',
    description: viewerCanPush
      ? 'There are no projects linked to this repository yet.'
      : 'There are no projects linked to this repository.',
    action: newProjectAction ? (
      <NewProjectForm owner={owner} repo={repo} action={newProjectAction}>
        <Blankslate.PrimaryAction type="submit">New project</Blankslate.PrimaryAction>
      </NewProjectForm>
    ) : undefined,
  }
}
